// Feature-Switch-System (Task, siehe TASKS.md „Feature-Switch-System"): Features bekommen eine
// Reifegrad-Grenze auf einer geordneten Umgebungs-Leiter dev(0) -> stage(1) -> beta(2) -> prod(3).
// Ein Feature ist aktiv, solange die aktuelle Umgebung NICHT hoeher liegt als sein Wert
// (Wert = hoechste Umgebung, in der es noch erscheinen darf): 'stage' -> dev+stage, 'prod' -> ueberall.
//
// Wichtig: CURRENT_ENV_STAGE liest import.meta.env.VITE_ENV_STAGE. Vite ersetzt das zur
// Build-Zeit statisch (kein echtes Runtime-Objekt), dadurch faltet der Minifier
// `RANK[CURRENT] <= RANK[X]` fuer ein konstantes X zu true/false und Tree-Shaking entfernt
// unerreichbare Zweige physisch aus dem Prod-Bundle - nicht nur eine Laufzeit-Prüfung.
export type EnvStage = 'dev' | 'stage' | 'beta' | 'prod';

const ENV_STAGE_RANK: Record<EnvStage, number> = {
    dev: 0,
    stage: 1,
    beta: 2,
    prod: 3,
};

const isEnvStage = (value: string | undefined): value is EnvStage =>
    value === 'dev' || value === 'stage' || value === 'beta' || value === 'prod';

// Fail-closed: fehlt/ist ungueltig VITE_ENV_STAGE, gilt die restriktivste Stufe ('prod') - ein
// vergessener Build-Arg blendet hoechstens zu viel aus, nie zu viel ein.
const rawEnvStage = import.meta.env.VITE_ENV_STAGE;
export const CURRENT_ENV_STAGE: EnvStage = isEnvStage(rawEnvStage) ? rawEnvStage : 'prod';

export type FeatureKey = 'imageExtractPose' | 'workflowBuilder' | 'prepaidPurchase' | 'creditsUi' | 'chatAttachments' | 'imageOutpaint';

// Wert = hoechste Umgebung, in der das Feature noch erscheinen darf ('prod' = ueberall live).
export const FEATURES: Record<FeatureKey, EnvStage> = {
    // Liefert laut SL-CostsService aktuell nur einen 0-Platzhalterpreis, soll vorerst nicht
    // angeboten werden (siehe TASKS.md, Task #92 „Offene Detailfragen").
    imageExtractPose: 'stage',
    // Demo-Zustand mit Mock-Daten (Kosten-Widgets nutzen Math.random()), nicht produktionsreif.
    workflowBuilder: 'stage',
    // Strategiewechsel (TASKS.md, 2026-07-22/09-08): zum Launch nur Abos, kein nutzer-sichtbarer
    // Prepaid-Kauf mehr. Serverseitiges Gegenstueck (PaymentService PREPAID_ENABLED) separat
    // beauftragt - dieses Flag blendet nur die UI aus. Seit 2026-09-21 (Nutzer) nur noch auf Dev
    // sichtbar: Prepaid wird auch auf Stage vorerst nicht mehr gebraucht.
    prepaidPurchase: 'dev',
    // Prod-Feedback-Runde 2026-09-21: Credits bleiben internes Metering, die ANZEIGE (Kosten an
    // Buttons, Coins-Widget, Kostenvorschauen, Credit-Salden auf /billing) entfaellt ueberall.
    // Auf Dev sichtbar, um das Metering weiter beobachten zu koennen. Verdrahtung: TASKS.md,
    // Masterplan Paket 3.
    creditsUi: 'dev',
    // Chat-API ist nicht multimodal - Anhang-UI bleibt auf Dev/Stage zum Weiterentwickeln,
    // Prod blendet sie aus (TASKS.md, Masterplan Paket 4).
    chatAttachments: 'stage',
    // ⛔ LIZENZ (2026-09-21, ../Legal/COMPLIANCE.md Abschnitt G): Outpaint/"Expand" laeuft auf
    // FLUX.1 Fill [dev] = Non-Commercial License - kommerzieller SaaS-Betrieb nicht gedeckt.
    // Auf Prod aus, bis BFL-Lizenz vorliegt oder der Workflow auf ein frei lizenziertes Modell
    // umgebaut ist. Die eigentliche Sperre sitzt serverseitig in cProxy (Modell-Policy) - dieses
    // Flag verhindert nur, dass Prod-Nutzer eine Funktion sehen, die der Server ablehnt.
    // NICHT auf 'prod' stellen, ohne dass Abschnitt G das Modell als freigegeben fuehrt.
    imageOutpaint: 'stage',
};

// Eine Rang-Logik fuer alles: Feature-Flags UND Registry-Eintraege (appRegistry.ts maxStage).
// Bugfix 2026-09-21: stand bis dahin als ">=" da - dadurch war auf Prod JEDES Flag aktiv und auf
// Dev jedes 'stage'-Flag aus (genau umgekehrt zur Definition oben).
export const isStageAllowed = (maxStage: EnvStage): boolean =>
    ENV_STAGE_RANK[CURRENT_ENV_STAGE] <= ENV_STAGE_RANK[maxStage];

export const isFeatureEnabled = (key: FeatureKey): boolean => isStageAllowed(FEATURES[key]);

// Fuer die Stage-Markierung (StageBadge): Ist etwas mit dieser Grenze auf Prod ausgeblendet? Auf
// Prod selbst ist die Frage gegenstandslos (dort erscheint es ja gar nicht).
export const isHiddenOnProd = (maxStage: EnvStage | undefined): boolean => (maxStage ?? 'prod') !== 'prod';
export const featureMaxStage = (key: FeatureKey): EnvStage => FEATURES[key];
export const highestStage = (stages: EnvStage[]): EnvStage =>
    stages.reduce<EnvStage>((best, s) => (ENV_STAGE_RANK[s] > ENV_STAGE_RANK[best] ? s : best), 'dev');
